import {
  Children,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import cn from 'classnames';
import FeatherIcon from 'feather-icons-react';
import { useClickOutside } from 'hooks/click-outside';
import PropTypes from 'prop-types';

import styles from './MultipleSelect.module.scss';

const SelectContext = createContext(null);

const useSelectContext = () => {
  const context = useContext(SelectContext);
  if (!context)
    throw new Error(
      `MultipleSelect compound components cannot be rendered outside the MultipleSelect component`,
    );
  return context;
};

export const MultipleSelect = ({
  value = [],
  defaultValue,
  placeholder = '',
  minSelect,
  maxSelect,
  useSearch = false,
  hideLabel = false,
  disabled = false,
  menuPlacement = 'bottom',
  children,
  className,
  onChange,
  error,
  noErrorMessage,
  dataTestId,
}) => {
  const wrapperRef = useRef(null);

  const [showMenu, setShowMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [optionsMap, setOptionsMap] = useState(new Map());
  const [validationError, setValidationError] = useState('');

  useClickOutside(wrapperRef, () => setShowMenu(false));

  // When value from form is empty but we have a non-empty defaultValue, sync it to the form
  // so the field registers and validation passes (avoids "required" error when default is shown but not in form state)
  const defaultSyncedRef = useRef(false);
  useEffect(() => {
    if (defaultSyncedRef.current) return;
    const valueEmpty =
      value === undefined || value === null || (Array.isArray(value) && value.length === 0);
    if (
      valueEmpty &&
      Array.isArray(defaultValue) &&
      defaultValue.length > 0 &&
      typeof onChange === 'function'
    ) {
      defaultSyncedRef.current = true;
      onChange(defaultValue);
    }
  }, [value, defaultValue, onChange]);

  useEffect(() => {
    const newOptionsMap = new Map();
    Children.forEach(children, child => {
      if (!child.props) return;
      newOptionsMap.set(child.props.value, {
        label: child.props.children,
        value: child.props.value,
      });
    });
    setOptionsMap(newOptionsMap);
  }, [children]);

  const selectedValues = useMemo(() => {
    let arr = [];
    const raw = value;
    if (raw != null) {
      if (Array.isArray(raw)) arr = raw;
      else if (
        typeof raw === 'object' &&
        !Array.isArray(raw) &&
        Object.keys(raw).every(k => /^\d+$/.test(k))
      ) {
        arr = Object.values(raw);
      } else {
        arr = [raw];
      }
    }
    if (arr.length === 0 && Array.isArray(defaultValue) && defaultValue.length > 0) {
      return defaultValue;
    }
    return arr;
  }, [value, defaultValue]);

  const selectedOptions = useMemo(() => {
    let values = [];
    if (!selectedValues) {
      console.warn('MultipleSelect: selectedValues should not be empty, got:', selectedValues);
      return [];
    }

    const options =
      optionsMap?.size > 0
        ? selectedValues
            .map(val => {
              const option = optionsMap.get(val);
              if (!option) {
                console.warn(`MultipleSelect: Option with value "${val}" not found in optionsMap`);
              }
              return option;
            })
            .filter(Boolean)
        : [];

    return options;
  }, [selectedValues, optionsMap]);

  const toggleOption = useCallback(
    option => {
      const isSelected = selectedValues.includes(option.value);
      const newSelectedValues = isSelected
        ? selectedValues.filter(v => v !== option.value)
        : [...selectedValues, option.value];

      if (isSelected && minSelect && newSelectedValues.length < minSelect) {
        setValidationError(
          `Please select at least ${minSelect} option${minSelect === 1 ? '' : 's'}`,
        );
        return;
      }

      if (!isSelected && maxSelect && newSelectedValues.length > maxSelect) {
        setValidationError(
          `Please select no more than ${maxSelect} option${maxSelect === 1 ? '' : 's'}`,
        );
        return;
      }

      setValidationError('');
      onChange(newSelectedValues);
    },
    [selectedValues, minSelect, maxSelect, onChange],
  );

  const contextValue = useMemo(
    () => ({ selectedValues, toggleOption, searchQuery }),
    [selectedValues, toggleOption, searchQuery],
  );

  return (
    <SelectContext.Provider value={contextValue}>
      <div
        ref={wrapperRef}
        className={cn(styles.root, 'min-w-0 max-w-full', className)}
        data-testid={dataTestId}>
        <div
          className={cn(styles.button, { [styles.disabled]: disabled })}
          data-testid="multiselect-trigger"
          onClick={() => {
            if (!disabled) setShowMenu(!showMenu);
          }}>
          <span className={styles.buttonLabel}>
            {!hideLabel && selectedOptions.length > 0 ? (
              selectedOptions.map(o => o.label).join(', ')
            ) : (
              <span className={styles.placeholder}>{placeholder}</span>
            )}
          </span>
        </div>
        <div
          className={cn(styles.menu, {
            [styles.menuVisible]: showMenu,
            [styles.menuTop]: menuPlacement === 'top',
          })}>
          {useSearch && (
            <>
              <div className={cn(styles.search)}>
                <FeatherIcon className={styles.icon} icon="search" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value.toLowerCase())}
                  className={styles.input}
                  placeholder="Search..."
                />
              </div>
            </>
          )}
          <ul>{children}</ul>
        </div>
        {error && !noErrorMessage && <span className={styles.error}>{error}</span>}
        {validationError && <span className={styles.error}>{validationError}</span>}
      </div>
    </SelectContext.Provider>
  );
};

MultipleSelect.propTypes = {
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.string, PropTypes.number]),
  defaultValue: PropTypes.array,
  placeholder: PropTypes.string,
  minSelect: PropTypes.number,
  maxSelect: PropTypes.number,
  useSearch: PropTypes.bool,
  hideLabel: PropTypes.bool,
  disabled: PropTypes.bool,
  menuPlacement: PropTypes.oneOf(['bottom', 'top']),
  children: PropTypes.node,
  className: PropTypes.string,
  onChange: PropTypes.func,
  error: PropTypes.string,
  noErrorMessage: PropTypes.bool,
};

const Item = ({ value, children, description, active }) => {
  const { selectedValues, toggleOption, searchQuery } = useSelectContext();
  const [filtered, setFiltered] = useState(false);

  useEffect(() => {
    const query = (searchQuery ?? '').toLowerCase();
    const label = children?.toString().toLowerCase() ?? '';
    const optionValue = String(value ?? '').toLowerCase();
    setFiltered(query !== '' && !label.includes(query) && !optionValue.includes(query));
  }, [searchQuery, children, value]);

  const handleSelected = () => {
    toggleOption({ label: children, value: value });
  };

  // Only sync selection from prop when parent explicitly passes active (e.g. Table, CustomFields).
  // When active is not passed (e.g. question-config MultiSelect), do nothing - avoid deselecting
  // pre-filled values (e.g. bundle details) that default would have treated as active=false.
  useEffect(() => {
    if (active === undefined) return;
    const isSelected = selectedValues.includes(value);
    if ((active && !isSelected) || (!active && isSelected)) {
      handleSelected();
    }
  }, [active]);

  return (
    <li
      className={cn(styles.item, { [styles.itemHidden]: filtered }, 'flex flex-col gap-2')}
      onClick={handleSelected}
      data-testid={`multiselect-option-${value}`}>
      <div className="flex min-w-0 items-start gap-2">
        <img
          src={`assets/images/icons/${selectedValues.includes(value) ? 'checkbox_checked' : 'checkbox_empty'}.svg`}
          className="mt-0.5 shrink-0"
          alt=""
        />
        <span className="min-w-0 flex-1 break-words leading-snug">{children}</span>
      </div>
      {description ? (
        <p className="text-muted small ml-6 min-w-0 break-words leading-snug">{description}</p>
      ) : null}
    </li>
  );
};

Item.propTypes = {
  value: PropTypes.any,
  description: PropTypes.string,
  children: PropTypes.node,
  active: PropTypes.bool,
};

MultipleSelect.Item = Item;
